import { keys, memoize } from 'lodash';
import moment from 'moment';
import 'moment-timezone';

export const StarfishTimeFormat = 'YYYY-MM-DD HH:mm';

const extJSDateFormatAtomToMomentFormatAtom = {
  'd': 'DD',    // Day of the month, 2 digits with leading zeros                             01 to 31
  'D': 'ddd',   // A short textual representation of the day of the week                     Mon to Sun
  'j': 'D',     // Day of the month without leading zeros                                    1 to 31
  'l': 'dddd',  // A full textual representation of the day of the week                      Sunday to Saturday
  'N': 'E',     // ISO-8601 numeric representation of the day of the week                    1 (for Monday) through 7 (for Sunday)
  'S': 'o',     // English ordinal suffix for the day of the month, 2 characters             st, nd, rd or th. Works well with j
  'w': 'd',     // Numeric representation of the day of the week                             0 (for Sunday) to 6 (for Saturday)
  'z': '!',     // (unsupported) The day of the year (starting from 0)                       0 to 364 (365 in leap years)
  'W': 'W',     // ISO-8601 week number of year, weeks starting on Monday                    01 to 53
  'F': 'MMMM',  // A full textual representation of a month, such as January or March        January to December
  'm': 'MM',    // Numeric representation of a month, with leading zeros                     01 to 12
  'M': 'MMM',   // A short textual representation of a month                                 Jan to Dec
  'n': 'M',     // Numeric representation of a month, without leading zeros                  1 to 12
  't': '!',     // Number of days in the given month                                         28 to 31
  'L': '!',     // Whether it's a leap year                                                  1 if it is a leap year, 0 otherwise.
  'o': 'Y',     // ISO-8601 year number (identical to (Y), but if the ISO week number (W)    Examples: 1998 or 2004
                // belongs to the previous or next year, that year is used instead)
  'Y': 'YYYY',  // A full numeric representation of a year, 4 digits                         Examples: 1999 or 2003
  'y': 'YY',    // A two digit representation of a year                                      Examples: 99 or 03
  'a': 'a',     // Lowercase Ante meridiem and Post meridiem                                 am or pm
  'A': 'A',     // Uppercase Ante meridiem and Post meridiem                                 AM or PM
  'g': 'h',     // 12-hour format of an hour without leading zeros                           1 to 12
  'G': 'H',     // 24-hour format of an hour without leading zeros                           0 to 23
  'h': 'hh',    // 12-hour format of an hour with leading zeros                              01 to 12
  'H': 'HH',    // 24-hour format of an hour with leading zeros                              00 to 23
  'i': 'mm',    // Minutes, with leading zeros                                               00 to 59
  's': 'ss',    // Seconds, with leading zeros                                               00 to 59
  'u': 'S',     // Decimal fraction of a second                                              Examples:
                // (minimum 1 digit, arbitrary number of digits allowed)                     001 (i.e. 0.001s) or
                // 100 (i.e. 0.100s) or
                // 999 (i.e. 0.999s) or
                // 999876543210 (i.e. 0.999876543210s)
  'O': 'ZZ',    // Difference to Greenwich time (GMT) in hours and minutes                   Example: +1030
  'P': 'Z',     // Difference to Greenwich time (GMT) with colon between hours and minutes   Example: -08:00
  'T': 'z',     // (problematic) Timezone abbreviation of the machine running the code       Examples: EST, MDT, PDT ...
  'Z': '!',     // (unsupported) Timezone offset in seconds (negative if west of UTC, positive if east)    -43200 to 50400
  'c': 'YYYY-MM-DD[T]HH:mm:ss[.]SSSZZ', // ISO 8601 date
  'U': 'X'      // Seconds since the Unix Epoch (January 1 1970 00:00:00 GMT)  1193432466 or -2138434463
};

const extFormats = new RegExp(keys(extJSDateFormatAtomToMomentFormatAtom).join('|'), 'g');

const uncachedMomentFormatForExtJSDateFormat = (extJSDateFormat) =>
  extJSDateFormat.replace(extFormats, extFormat => extJSDateFormatAtomToMomentFormatAtom[extFormat] || ' ');

/**
 * Convert an Ext JS 3.4 date format into a moment.js format. Not all ExtJS date formats are supported: see
 * the list of formats above with '!'.
 *
 * @param {string} extJSDateFormat an ExtJS date format such as "Y-m-d"
 * @returns {string} the equivalent moment.js date format, such as "YYYY-MM-DD".
 */
export const momentFormatForExtJSDateFormat = memoize(uncachedMomentFormatForExtJSDateFormat);

// RFC3339 formatting of date
export const formatRFC3339 = (date) => !date ? '' : date.format("YYYY-MM-DDTHH:mm:ssZ");

/**
 * Convert a given amount of minutes in an object with hours and minutes
 * example: 128 minutes = 2 hours and 8 minutes
 *
 * @param {number} timeInMinutes an given amount of time in minutes
 * @return {object} with two fields -- hours and minutes
 * */
export const getTimeInHours = (timeInMinutes) => {
  if (timeInMinutes < 0) return {hours: 0, minutes: 0};
  return {
    hours: Math.floor(timeInMinutes / 60),
    minutes: timeInMinutes % 60
  };
};

/**
 * Returns the active Starfish tenant's preferred date/time format
 * for the given type of date-time value, which may be one of:
 * "date", "month", "time", "dateTime", or "weekdayDate", or
 * optionally, one of those values suffixed with "DisplayFormat",
 * such as "dateTimeDisplayFormat".
 *
 * Requesting an unknown date/time format will raise an error.
 *
 * Prefer this function to the less-descriptively named alias
 * [getDateFormat].
 *
 * @param {string} type the type of date-time value to format.
 * @return {string} the preferred tenant date-time format.
 */
export function tenantTimeFormat(type = 'date') {
    if (!/DisplayFormat$/.test(type)) {
        type = `${type}DisplayFormat`;
    }
    const extTimeFormat = sfTenant.language.getDateFormat(type);
    if (!extTimeFormat) {
        throw new Error(`unknown date-time format ${type}`);
    }
    return momentFormatForExtJSDateFormat(extTimeFormat);
}

/**
 * Deprecated alias for tenantTimeFormat.
 */
export const getDateFormat = tenantTimeFormat;

export const getDisplayDate = (date) => {
  const dateToDisplay = moment(date);
  if (dateToDisplay.isSame(moment(), 'day')) return 'Today';
  if (dateToDisplay.isSame(moment().subtract(1, 'd'), 'day')) return 'Yesterday';
  if (dateToDisplay.isSame(moment().add(1, 'd'), 'day')) return 'Tomorrow';

  return moment(date).format(tenantTimeFormat('date'));
}

/**
 * Given a moment object or an unambiguous date-time string,
 * returns a moment object, parsing the date-time string if
 * necessary.
 *
 * @param {moment|string} momentOrDateTimeString the moment or date-time string
 * @return {moment} the parsed moment
 */
export const asMoment = (momentOrDateTimeString) =>
  (momentOrDateTimeString instanceof moment)
    ? momentOrDateTimeString
    : moment(momentOrDateTimeString);

/**
 * Given a [zone], returns a function that accepts a moment object
 * or an unambiguously-parseable date-time string, and converts it
 * into that time zone.
 *
 * If no [zone] is supplied, returns the moment unmodified.
 *
 * @param {string} zone an IANA timezone name, possibly falsy
 * @return {function} a function that accepts a date-time string or moment
 *                    and converts it into the given time-zone.
 */
export const inZone = (zone) =>
  !zone
    ? asMoment
    : (momentOrDateTimeString) => asMoment(momentOrDateTimeString).tz(zone);

/**
 * Returns a date-time in the user profile timezone, if available.
 * Otherwise returns the date-time in the user's browser timezone.
 *
 * @param {moment|dateTime} dateTime in RFC3339 ex. 2019-08-13T13:15:14.241-04:00
 * @return {object} date-time moment in the user's profile time zone.
 */
export const getDateTimeInUserTimeZone = (dateTime) => inZone(typeof sfUser !== "undefined" && sfUser.timeZone)(dateTime);

export const hoursOfTheDayOptions = [
  { label: '12:00 am', value: '12:00 AM' },
  { label: '1:00 am', value: '1:00 AM' },
  { label: '2:00 am', value: '2:00 AM' },
  { label: '3:00 am', value: '3:00 AM' },
  { label: '4:00 am', value: '4:00 AM' },
  { label: '5:00 am', value: '5:00 AM' },
  { label: '6:00 am', value: '6:00 AM' },
  { label: '7:00 am', value: '7:00 AM' },
  { label: '8:00 am', value: '8:00 AM' },
  { label: '9:00 am', value: '9:00 AM' },
  { label: '10:00 am', value: '10:00 AM' },
  { label: '11:00 am', value: '11:00 AM' },
  { label: '12:00 pm', value: '12:00 PM' },
  { label: '1:00 pm', value: '1:00 PM' },
  { label: '2:00 pm', value: '2:00 PM' },
  { label: '3:00 pm', value: '3:00 PM' },
  { label: '4:00 pm', value: '4:00 PM' },
  { label: '5:00 pm', value: '5:00 PM' },
  { label: '6:00 pm', value: '6:00 PM' },
  { label: '7:00 pm', value: '7:00 PM' },
  { label: '8:00 pm', value: '8:00 PM' },
  { label: '9:00 pm', value: '9:00 PM' },
  { label: '10:00 pm', value: '10:00 PM' },
  { label: '11:00 pm', value: '11:00 PM' }
];

// Hours of the day in 15 minute interval along with the timezone.
// Example (if timezone is CT) this starts with {label: "12:00 AM CT", value: "00:00"}, {label: "12:15 AM CT" value: "00:15"}
// and ending with {label: "11:45 PM CT" value: "23:45"}
export const fifteenMinuteIntervalSlotsForDay = (timezone) => {
  let arr = [];
  const totalIterations = 4 * 24;
  for (let i = 0; i < totalIterations; i++) {
    arr.push({
      label: moment(
          {
            hour: Math.floor(i * 0.25),
            minutes: (i * 15) % 60
          }).format("h:mm A") + ' ' + timezone,
      value: moment(
          {
            hour: Math.floor(i * 0.25),
            minutes: (i * 15) % 60
          }).format("HH:mm")
    })
  }
  return arr
}

export const daysOfTheWeekOptions = [
  { label: 'Sunday', value: 'Sunday' },
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
];

export const minAppointmentTime = [
  { label: '15 minutes', value: 15},
  { label: '20 minutes', value: 20 },
  { label: '30 minutes', value: 30},
  { label: '40 minutes', value: 40},
  { label: '45 minutes', value: 45 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: '6 hours', value: 360 },
  { label: '8 hours', value: 480 }
];
